import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"

export function EndNode({ id, data, selected }: NodeProps) {
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const hasOutput = isCompleted && !!output

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[150px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[3])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="text-center text-sm font-medium">
            End
            <NodeEditDialog description="This node returns the final flow output." title="End node">
              <div className="text-muted-foreground text-sm">
                The End node receives the final upstream output and returns it as the flow result.
              </div>
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-2">
          <div className="text-muted-foreground text-center text-xs">Output</div>
          <Handle position={Position.Left} type="target" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
